import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, ClipboardCheck, Truck, Scale, X, CheckCircle2, Clock } from "lucide-react";
import { useAuth } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShiftProductionPage = () => {
  const { user, currentCompany } = useAuth() || {};
  const isSupervisorPlus = ["supervisor", "manager", "admin", "office_staff", "super_admin"].includes(user?.role);

  const [reports, setReports] = useState([]);
  const [machines, setMachines] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    shift_date: today,
    job_id: "",
    machine_id: "",
    start_hours: "",
    finish_hours: "",
    trucks_tipped: 0,
    tonnes_shifted: 0,
    material_type: "",
    downtime_minutes: 0,
    downtime_reason: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, m, j] = await Promise.all([
        axios.get(`${API}/mcs/shift-production`),
        axios.get(`${API}/machines`),
        axios.get(`${API}/jobs?status=active`),
      ]);
      setReports(r.data || []);
      setMachines(m.data || []);
      setJobs(j.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submit = async () => {
    if (!form.shift_date) return toast.error("Shift date required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        start_hours: form.start_hours === "" ? null : Number(form.start_hours),
        finish_hours: form.finish_hours === "" ? null : Number(form.finish_hours),
        trucks_tipped: Number(form.trucks_tipped) || 0,
        tonnes_shifted: Number(form.tonnes_shifted) || 0,
        downtime_minutes: Number(form.downtime_minutes) || 0,
        job_name: jobs.find(j => j.id === form.job_id)?.name || null,
        machine_label: machines.find(m => m.id === form.machine_id)?.rego || machines.find(m => m.id === form.machine_id)?.name || null,
      };
      await axios.post(`${API}/mcs/shift-production`, payload);
      toast.success("Production report submitted");
      setShowCreate(false);
      setForm({ shift_date: today, job_id: "", machine_id: "", start_hours: "", finish_hours: "", trucks_tipped: 0, tonnes_shifted: 0, material_type: "", downtime_minutes: 0, downtime_reason: "", notes: "" });
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally { setSaving(false); }
  };

  const signOff = async (id) => {
    if (!window.confirm("Sign off this production report?")) return;
    try {
      await axios.post(`${API}/mcs/shift-production/${id}/sign-off`);
      toast.success("Signed off");
      await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="shift-production-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight text-slate-900 font-heading">Shift Production</h1>
          <p className="text-sm text-slate-500 mt-1">Daily trucks tipped, tonnes shifted, downtime. Supervisor sign-off required.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-semibold" data-testid="create-shift-production-btn">
          <Plus className="w-5 h-5" /> New Report
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading…</div>
      ) : reports.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-md text-slate-400">No production reports yet. Click "New Report" to submit one.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {reports.map(r => (
            <div key={r.id} className="bg-white border-2 border-slate-200 hover:border-orange-300 rounded-md p-4" data-testid={`report-card-${r.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-bold text-slate-900">{r.shift_date}</div>
                  <div className="text-xs text-slate-500">{r.operator_name} {r.job_name && `· ${r.job_name}`}</div>
                </div>
                <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded ${r.status === "signed_off" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                  {r.status === "signed_off" ? "Signed off" : "Awaiting sign-off"}
                </span>
              </div>
              {r.machine_label && <div className="text-xs text-slate-600 mb-1">Machine: <span className="font-semibold">{r.machine_label}</span></div>}
              <div className="grid grid-cols-3 gap-2 text-xs mt-2">
                <div className="bg-slate-50 rounded p-2 text-center">
                  <Truck className="w-4 h-4 mx-auto mb-0.5 text-slate-500" />
                  <div className="font-bold">{r.trucks_tipped || 0}</div>
                  <div className="text-[10px] text-slate-500 uppercase">Trucks</div>
                </div>
                <div className="bg-slate-50 rounded p-2 text-center">
                  <Scale className="w-4 h-4 mx-auto mb-0.5 text-slate-500" />
                  <div className="font-bold">{r.tonnes_shifted || 0}t</div>
                  <div className="text-[10px] text-slate-500 uppercase">Shifted</div>
                </div>
                <div className="bg-slate-50 rounded p-2 text-center">
                  <Clock className="w-4 h-4 mx-auto mb-0.5 text-slate-500" />
                  <div className="font-bold">{r.downtime_minutes || 0}m</div>
                  <div className="text-[10px] text-slate-500 uppercase">Downtime</div>
                </div>
              </div>
              {r.downtime_reason && <div className="text-xs text-amber-700 mt-2"><span className="font-semibold">Downtime:</span> {r.downtime_reason}</div>}
              {r.notes && <div className="text-xs text-slate-600 mt-2 italic">{r.notes}</div>}
              {isSupervisorPlus && r.status !== "signed_off" && (
                <button onClick={() => signOff(r.id)} className="mt-3 w-full px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-semibold inline-flex items-center justify-center gap-1" data-testid={`sign-off-${r.id}`}>
                  <CheckCircle2 className="w-4 h-4" /> Sign Off
                </button>
              )}
              {r.signed_off_by_name && <div className="text-[10px] text-emerald-700 mt-2">Signed off by {r.signed_off_by_name}</div>}
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="create-shift-production-modal">
          <div className="bg-white rounded-md shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="border-b-2 border-slate-200 p-5 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold">New Production Report</h2>
              <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-700"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={form.shift_date} onChange={(e) => setForm({...form, shift_date: e.target.value})} className="border-2 border-slate-200 rounded px-3 py-2" data-testid="sp-date" />
                <select value={form.job_id} onChange={(e) => setForm({...form, job_id: e.target.value})} className="border-2 border-slate-200 rounded px-3 py-2" data-testid="sp-job">
                  <option value="">-- Job --</option>
                  {jobs.map(j => <option key={j.id} value={j.id}>{j.name}</option>)}
                </select>
              </div>
              <select value={form.machine_id} onChange={(e) => setForm({...form, machine_id: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="sp-machine">
                <option value="">-- Machine --</option>
                {machines.map(m => <option key={m.id} value={m.id}>{m.rego || m.name}{m.type ? ` (${m.type})` : ""}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold uppercase text-slate-500">Start hrs</label>
                  <input type="number" step="0.1" value={form.start_hours} onChange={(e) => setForm({...form, start_hours: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="sp-start" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-slate-500">Finish hrs</label>
                  <input type="number" step="0.1" value={form.finish_hours} onChange={(e) => setForm({...form, finish_hours: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="sp-finish" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold uppercase text-slate-500">Trucks tipped</label>
                  <input type="number" min="0" value={form.trucks_tipped} onChange={(e) => setForm({...form, trucks_tipped: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="sp-trucks" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-slate-500">Tonnes</label>
                  <input type="number" step="0.1" min="0" value={form.tonnes_shifted} onChange={(e) => setForm({...form, tonnes_shifted: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="sp-tonnes" />
                </div>
              </div>
              <input type="text" placeholder="Material type (e.g. road base)" value={form.material_type} onChange={(e) => setForm({...form, material_type: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold uppercase text-slate-500">Downtime (min)</label>
                  <input type="number" min="0" value={form.downtime_minutes} onChange={(e) => setForm({...form, downtime_minutes: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="sp-downtime" />
                </div>
                <input type="text" placeholder="Downtime reason" value={form.downtime_reason} onChange={(e) => setForm({...form, downtime_reason: e.target.value})} className="border-2 border-slate-200 rounded px-3 py-2" />
              </div>
              <textarea placeholder="Notes…" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} className="w-full border-2 border-slate-200 rounded px-3 py-2" />
            </div>
            <div className="border-t-2 border-slate-200 p-5 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setShowCreate(false)} disabled={saving} className="flex-1 px-4 py-3 border-2 border-slate-200 hover:border-slate-400 rounded-md font-semibold">Cancel</button>
              <button onClick={submit} disabled={saving} className="flex-1 px-4 py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-md font-semibold" data-testid="sp-submit">
                {saving ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftProductionPage;
